import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FaChevronLeft, FaChevronRight, FaPlay } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { encryptId } from '../SecurityComponent/cryptoUtils';
import './trending-top10.scss';

const POPUP_W = 320;

// Card "Top 10 xu hướng" kiểu FPT Play: poster dọc + số thứ hạng to (nghiêng, bạc) đè
// góc dưới-trái, tên overlay đáy. Hover hiện popup (tái dùng CSS .mc-popup của MovieCard).
const TrendingTop10List = ({ type = 'trending', size = 10 }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const [popup, setPopup] = useState(null); // { item, style }
    const [vipIds, setVipIds] = useState(() => new Set());
    const swiperRef = useRef(null);
    const showTimer = useRef(null);
    const hideTimer = useRef(null);

    useEffect(() => {
        setLoading(true);
        fetch(`/movies-public-api.php?type=${encodeURIComponent(type)}&page=0&size=${size}`)
            .then(r => r.json())
            .then(d => { if (d.success) setItems(d.content || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [type, size]);

    // Danh sách phim VIP (giống MovieCard) để gắn badge VIP lên thẻ
    useEffect(() => {
        fetch('/movies-featured-api.php?vipIds=1')
            .then(r => r.json())
            .then(d => setVipIds(new Set((d.ids || []).map(Number))))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const onScroll = () => setPopup(null);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const updateNav = s => { setIsBeginning(s.isBeginning); setIsEnd(s.isEnd); };
    const cancelHide = () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
    const scheduleHide = () => {
        if (showTimer.current) clearTimeout(showTimer.current);
        hideTimer.current = setTimeout(() => setPopup(null), 200);
    };
    const handleEnter = (item, e) => {
        cancelHide();
        const el = e.currentTarget;
        showTimer.current = setTimeout(() => {
            const rect = el.getBoundingClientRect();
            let left = rect.left + rect.width / 2 - POPUP_W / 2;
            if (left < 8) left = 8;
            if (left + POPUP_W > window.innerWidth - 8) left = window.innerWidth - POPUP_W - 8;
            setPopup({ item, style: { position: 'fixed', top: rect.top, left, width: POPUP_W } });
        }, 400);
    };

    const watchUrl = (slug) => `/watch/${slug}/${encryptId(1)}`;

    if (loading) return <div style={{ padding: '20px 0', color: '#555' }}>Đang tải...</div>;
    if (!items.length) return null;

    const p = popup?.item;
    const meta = p ? [p.releaseYear, p.episodeCount > 1 ? `${p.episodeCount} tập` : null, p.country].filter(Boolean) : [];
    const popupEl = popup && ReactDOM.createPortal(
        <div className="mc-popup" style={{ ...popup.style }} onMouseEnter={cancelHide} onMouseLeave={scheduleHide}>
            <div className="mc-popup__backdrop" style={{ backgroundImage: `url(${p.posterUrl || p.thumbnailUrl})` }} />
            <div className="mc-popup__body">
                <div className="mc-popup__actions">
                    <button className="mc-popup__play" onClick={() => { window.location.href = watchUrl(p.slug); }}>
                        <FaPlay size={10} /> Xem ngay
                    </button>
                </div>
                <p className="mc-popup__title">{p.title}</p>
                <div className="mc-popup__rating">
                    {p.averageRating > 0 && <span className="mc-star">⭐ {Number(p.averageRating).toFixed(1)}</span>}
                    {p.episodeCount > 1 && <span className="mc-badge">{p.episodeCount} tập</span>}
                    <span className="mc-badge hd">HD</span>
                </div>
                {meta.length > 0 && <p className="mc-popup__meta">{meta.join(' · ')}</p>}
                {p.description
                    ? <p className="mc-popup__desc" dangerouslySetInnerHTML={{ __html: p.description.replace(/<[^>]+>/g, '') }} />
                    : p.genres?.length > 0 && <p className="mc-popup__desc">{p.genres.map(g => g.name).join(' · ')}</p>}
            </div>
        </div>,
        document.body
    );

    return (
        <div className="trend-top10">
            {!isBeginning && (
                <button className="trend-top10__nav trend-top10__nav--prev" onClick={() => {
                    const s = swiperRef.current;
                    if (!s) return;
                    const step = s.slidesPerViewDynamic?.() || 1;
                    s.slideTo(Math.max(0, s.activeIndex - step));
                }}>
                    <FaChevronLeft />
                </button>
            )}
            {!isEnd && (
                <button className="trend-top10__nav trend-top10__nav--next" onClick={() => {
                    const s = swiperRef.current;
                    if (!s) return;
                    const step = s.slidesPerViewDynamic?.() || 1;
                    s.slideTo(s.activeIndex + step);
                }}>
                    <FaChevronRight />
                </button>
            )}
            <Swiper
                grabCursor
                spaceBetween={16}
                slidesPerView="auto"
                onSwiper={s => { swiperRef.current = s; updateNav(s); }}
                onSlideChange={updateNav}
                onActiveIndexChange={updateNav}
                onReachBeginning={updateNav}
                onReachEnd={updateNav}
                onResize={updateNav}
            >
                {items.map((item, i) => {
                    const rank = i + 1;
                    // thumbnailUrl = ảnh DỌC (2:3 poster); posterUrl thực ra là ảnh NGANG (backdrop)
                    const poster = item.thumbnailUrl || item.posterUrl;
                    const badge = item.episodeCount > 1 ? `${item.episodeCount} tập` : (item.quality || 'HD');
                    const isVip = !!item.restrictedAccess || vipIds.has(Number(item.id));
                    return (
                        <SwiperSlide key={item.id} className="trend-top10__slide">
                            <Link
                                to={watchUrl(item.slug)}
                                className="trend-card"
                                onMouseEnter={(e) => handleEnter(item, e)}
                                onMouseLeave={scheduleHide}
                            >
                                <div className="trend-card__poster">
                                    {poster
                                        ? <img src={poster} alt={item.title} loading="lazy" decoding="async" />
                                        : <div className="trend-card__noimg">🎬</div>}
                                    <span className="trend-card__badge">{badge}</span>
                                    {isVip && <span className="trend-card__vip">VIP</span>}
                                    <div className="trend-card__play"><FaPlay /></div>
                                    <div className="trend-card__overlay">
                                        <span className="trend-card__name">{item.title}</span>
                                    </div>
                                    <span className={`trend-card__rank${rank >= 10 ? ' trend-card__rank--two' : ''}`}>{rank}</span>
                                </div>
                            </Link>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
            {popupEl}
        </div>
    );
};

export default TrendingTop10List;
